import React from 'react';
import Button from '../../../../../components/button';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { canApproveOrReject, canEdit } from '../logic';
import { CompensationReview } from '../types';

type Props = {
  review: CompensationReview;
  isProcessing: boolean;
  // Blocks submit (with an explanatory tooltip) while the leaf is over budget.
  submitDisabled?: boolean;
  submitDisabledTooltip?: string;
  onSubmit: () => void;
  onApprove: () => void;
  onReject: () => void;
};

// Header actions, gated by the running user's role and the review status:
// reviewer + PREPARING → Submit; approver + SUBMITTED → Approve / Reject.
export const CompensationReviewActions: React.FC<
  React.PropsWithChildren<Props>
> = ({
  review,
  isProcessing,
  submitDisabled,
  submitDisabledTooltip,
  onSubmit,
  onApprove,
  onReject,
}) => {
  const t = useNamespacedTranslation('compensationReviews.actions');

  if (canEdit(review)) {
    return (
      <Button
        variant="primary"
        label={t('submitForApproval')}
        icon="arrow-forward-outline"
        isProcessing={isProcessing}
        disabled={submitDisabled}
        tooltip={submitDisabled ? submitDisabledTooltip : undefined}
        onClick={onSubmit}
      />
    );
  }

  if (canApproveOrReject(review)) {
    return (
      <div className="d-flex gap-2">
        <Button
          variant="outline-primary"
          label={t('approve')}
          icon="checkmark-outline"
          isProcessing={isProcessing}
          onClick={onApprove}
        />
        <Button
          variant="outline-danger"
          label={t('reject')}
          icon="close-outline"
          isProcessing={isProcessing}
          onClick={onReject}
        />
      </div>
    );
  }

  return null;
};
